import {FC, useEffect, useMemo, useState} from 'react';
import {withMask} from 'use-mask-input';
import {useOutClick} from 'app/lib/hooks/useOutClick';

import {
  DrawerProps,
  IDictionary,
  NewEntrieFormValues,
  NewEventFormValues,
  Owner,
} from 'shared/types/common';
import {Drawer} from 'shared/ui/drawer';
import {AddLarge, CloseIcon} from 'shared/ui/icons';
import {toRem} from 'shared/helpers';
import {useNewEntrie} from 'features/diary/lib/useNewEntrie';
import {Controller} from 'react-hook-form';

import {
  NewEntrieBody,
  NewEntrieBorder,
  NewEntrieDelete,
  NewEntrieDictionariesPopover,
  NewEntrieFilesWrapper,
  NewEntrieFooter,
  NewEntrieHead,
  NewEntrieImage,
  NewEntrieLength,
  NewEntriePlus,
  NewEntrieRow,
  NewEntrieSubtitle,
  NewEntrieTextAreaWrapper,
  NewEntrieTitle,
  NewEntrieWrapper,
} from '../styles';

import {IDropdownMenuItem} from '@atomaro/ui-kit/components/DropdownMenu/types';

import Button from '@atomaro/ui-kit/components/Button/Button/Button';
import InputDate from '@atomaro/ui-kit/components/InputDate/InputDate';
import TagItem from '@atomaro/ui-kit/components/Tag/TagItem/TagItem';
import InputNumberStepper from '@atomaro/ui-kit/components/InputNumberStepper/InputNumberStepper';
import TagGroup from '@atomaro/ui-kit/components/Tag/TagGroup/TagGroup';

import profileImg from 'shared/assets/img/user.png';
import {COLORS, notifySelectItems} from 'shared/consts';
import {toast} from 'react-toastify';
import {Spacer} from 'shared/ui/spacer';
import {CalendarNewEvent} from 'features/calendar';
import {format} from 'date-fns';
import {DiaryCustomFields} from '../diaryCustomFeilds/ui/DiaryCustomFields';
import {useDiaryStore} from 'entities/diary';
import {HivesSelectWithActions} from './HivesSelectWithActions';
import {utcToUserDate} from 'features/calendar/helpers/dates.ts';

type DiaryNewEntrieProps = {
  drawerProps: DrawerProps;
  defaultValues?: Partial<NewEntrieFormValues>;
  defaultAuthor?: Owner;
  defaultDictionaries?: IDictionary[];
  defaultAttachments?: string[];
  editId?: string | number;
  isMobile?: boolean;
  editEventValues?: (NewEventFormValues & {id: string}) | null;
};

export const DiaryNewEntrie: FC<DiaryNewEntrieProps> = ({
  drawerProps,
  defaultValues = {
    inspectedAt: new Date(),
    hives: [],
    frames: 0,
    strength: 0,
    brood: 0,
    note: '',
    attachmentIds: [],
    fields: [],
  },
  defaultAuthor,
  defaultDictionaries,
  defaultAttachments,
  editId,
  editEventValues = null,
  isMobile,
}) => {
  const [hiveItems, setHiveItems] = useState<IDropdownMenuItem[]>([]);

  const customFields = useDiaryStore((s) => s.customFields);
  const setCustomFields = useDiaryStore((s) => s.setCustomFields);
  const customFieldsCols = useDiaryStore((s) => s.customFieldsCols);

  const {
    control,
    activeDictionaries,
    showDictionaries,
    isDictionariesPopoverOpened,
    dictionariesRef,
    errors,
    noteLength,
    fileInputRef,
    attachments,
    userInfo,
    isLoading,
    hives,
    noteHeight,
    formValues,
    isТewEventOpen,
    entrieEvent,
    entrieEventHives,
    entrieEventHiveIds,
    setIsNewEventOpen,
    onDateChange,
    onHiveChange,
    onFramesChange,
    onStrengthChange,
    onBroodChange,
    handleSubmit,
    onSubmit,
    onActiveDictionariesInputChange,
    setIsDictionariesPopoverOpened,
    onActiveDictionaryAdd,
    onActiveDictionaryRemove,
    onDictionaryClick,
    reset,
    onNoteChange,
    onFileChange,
    onFileRemove,
    clearDictionaries,
    onNewEventSubmit,
    setCustomFieldValue,
    commonDictionaryIds,
    customFieldValues,
    customDictionaryType,
    customDictionaryIds,
    customShowDictionaries,
    customIsDictionariesPopoverOpened,
    onCustomDictionariesInputChange,
    onCustomDictionaryAdd,
    onCustomDictionaryRemove,
    onCustomDictionaryClick,
    setCustomIsDictionariesPopoverOpenedSafe,
    tempFieldIdsRef,
    setCustomIsDictionariesPopoverOpened,
  } = useNewEntrie({
    defaultValues,
    defaultAuthor,
    defaultDictionaries,
    defaultAttachments,
    editId,
    editEventValues,
    fields: customFields,
  });

  useEffect(() => {
    setHiveItems(hives.map((hive) => ({value: `№${hive.number}`, key: hive.id})));
  }, [hives]);

  useEffect(() => {
    if (drawerProps.isOpen) {
      reset({
        ...defaultValues,
      });
    }
  }, [drawerProps.isOpen, editId, reset]);

  useOutClick(dictionariesRef, () => {
    //console.log('useOutClick dictionariesRef');
    setIsDictionariesPopoverOpened(false);
    setCustomIsDictionariesPopoverOpened({});
  });

  const onReset = () => {
    drawerProps.onClose();
    clearDictionaries();
    const tempIds = tempFieldIdsRef.current;

    if (!tempIds.length) return;

    const cleaned = (customFields ?? []).filter((field) => !tempIds.includes(field.id));

    setCustomFields(cleaned);

    tempFieldIdsRef.current = [];
    reset();
  };

  const onFormSubmit = async (data: NewEntrieFormValues) => {
    //console.log("onFormSubmit:", data);
    //console.log("customFieldValues:", customFieldValues);
    //console.log("customIsDictionariesPopoverOpened:", customIsDictionariesPopoverOpened);
    const res = await onSubmit(data);

    // const statuses = Array.isArray(res) ? res : [res];

    // const isSuccess =
    //   statuses.length > 0 && statuses.every((s) => s === 200 || s === 201);
    if (res) {
      toast.success(`Запись успешно ${editId ? 'отредактирована' : 'добавлена'}`);
      drawerProps.onClose();
      clearDictionaries();
    } else {
      toast.error('Произошла ошибка, попробуйте позже, пожалуйста');
    }
  };

  const userAvatarSrc = useMemo(() => {
    const userAvatarId = userInfo?.profile?.avatar?.id;
    if (userAvatarId) {
      return `/ecpp/attachments/files?attachmentId=${userAvatarId}`;
    }

    return profileImg;
  }, [userInfo, profileImg]);

  const firstNotify = useMemo(() => {
    if (!entrieEvent?.notifyAt) {
      return 'Нет';
    }
    const notifDate = new Date(entrieEvent.notifyAt);
    const start = new Date(entrieEvent.startAt);

    const diff = notifDate.getTime() - start.getTime();

    const finded = notifySelectItems.find((el) => el?.key === diff);
    if (!finded) return 'Нет';

    return finded.value;
  }, [entrieEvent]);

  const secondNotify = useMemo(() => {
    if (!entrieEvent?.notifyAtRepeat) {
      return 'Нет';
    }
    const notifDate = new Date(entrieEvent.notifyAtRepeat);
    const start = new Date(entrieEvent.startAt);

    const diff = notifDate.getTime() - start.getTime();

    const finded = notifySelectItems.find((el) => el?.key === diff);
    if (!finded) return 'Нет';

    return finded.value;
  }, [entrieEvent]);

  const calendarDefaultValues = useMemo(() => {
    return {
      ...editEventValues,
      hives: entrieEventHives,
      hiveIds: entrieEventHiveIds,
    };
  }, [editEventValues, entrieEventHives, entrieEventHiveIds]);

  return (
    <>
      <Drawer isOpen={drawerProps.isOpen} position={drawerProps.position} onClose={onReset}>
        <NewEntrieWrapper
          onSubmit={handleSubmit(onFormSubmit)}
          className={isLoading ? '_skeleton' : ''}
        >
          <NewEntrieHead $isMobile={isMobile}>
            <Button
              iconPrefix={<CloseIcon />}
              variant={isMobile ? 'ghost' : 'secondary'}
              colorScheme="neutral"
              style={{
                alignSelf: 'flex-end',
                width: isMobile ? toRem(24) : toRem(36),
              }}
              size={isMobile ? 's' : 'm'}
              onClick={drawerProps.onClose}
            />
            <NewEntrieTitle>{editId ? 'Редактирование записи' : 'Новая запись'}</NewEntrieTitle>
            {isMobile && <Spacer width={toRem(24)} height={toRem(24)} />}
          </NewEntrieHead>
          <NewEntrieBody>
            <NewEntrieRow>
              <span>Дата осмотра</span>
              <Controller
                control={control}
                name="inspectedAt"
                render={({field}) => (
                  <InputDate
                    maxDate={new Date()}
                    activeDate={field.value}
                    maxYear={new Date().getFullYear()}
                    ref={withMask('99.99.9999')}
                    onChange={onDateChange}
                  />
                )}
              />
            </NewEntrieRow>
            <NewEntrieRow>
              <span>Номер улья</span>
              <Controller
                name="hives"
                control={control}
                rules={{
                  validate: (value) =>
                    value && value.length > 0 ? true : 'Необходимо выбрать улей',
                }}
                render={({field}) => {
                  return (
                    <HivesSelectWithActions
                      style={{width: toRem(220)}}
                      value={field.value ?? []}
                      items={hiveItems}
                      placeholder="Номер улья"
                      onChange={(newValue) => {
                        field.onChange(newValue);
                        onHiveChange?.(newValue);
                      }}
                      error={errors.hives?.message}
                    />
                  );
                }}
              />
            </NewEntrieRow>

            <NewEntrieBorder />

            {userInfo && (
              <>
                <NewEntrieRow>
                  <span>Сотрудник</span>
                  <TagItem>
                    <img src={userAvatarSrc} alt={userInfo.name.first} />
                    <span>{userInfo.name.first}</span>
                  </TagItem>
                </NewEntrieRow>
                <NewEntrieBorder />
              </>
            )}

            {formValues.hives.length !== 0 && (
              <>
                {formValues.hives.length === 1 && (
                  <>
                    <NewEntrieRow>
                      <span>Всего рамок </span>
                      <Controller
                        name="frames"
                        control={control}
                        rules={
                          formValues.hives.length > 1
                            ? undefined
                            : {
                                min: {value: 1, message: 'Укажите количество рамок'},
                              }
                        }
                        render={({field}) => (
                          <InputNumberStepper
                            name={field.name}
                            value={field.value}
                            min={0}
                            disabled={formValues.hives.length > 1 ? 'all' : undefined}
                            defaultValue={field.value}
                            onChange={onFramesChange}
                            error={errors.frames?.message}
                            hint={
                              formValues.hives.length > 1
                                ? field.value > 0
                                  ? 'Одинаковые знач.'
                                  : 'Разные значения'
                                : ''
                            }
                          />
                        )}
                      />
                      {/* {showFramesReset && 
              <Button 
                label="Сбросить"
                iconSuffix={<ResetIcon />}
                variant='ghost'
                colorScheme='neutral'
                onClick={() => onFramesChange(0)}
              />
              } */}
                    </NewEntrieRow>
                    <NewEntrieRow>
                      <span>Сила семьи</span>
                      <Controller
                        name="strength"
                        control={control}
                        rules={
                          formValues.hives.length > 1
                            ? undefined
                            : {min: {value: 1, message: 'Укажите силу семьи'}}
                        }
                        render={({field}) => (
                          <InputNumberStepper
                            name={field.name}
                            value={field.value}
                            disabled={formValues.hives.length > 1 ? 'all' : undefined}
                            defaultValue={field.value}
                            min={0}
                            max={formValues.frames}
                            onChange={onStrengthChange}
                            error={errors.strength?.message}
                            hint={
                              formValues.hives.length > 1
                                ? field.value > 0
                                  ? 'Одинаковые знач.'
                                  : 'Разные значения'
                                : ''
                            }
                          />
                        )}
                      />
                      {/* {showStrengthReset && 
              <Button 
                label="Сбросить"
                iconSuffix={<ResetIcon />}
                variant='ghost'
                colorScheme='neutral'
                onClick={() => onStrengthChange(0)}
              />
              } */}
                    </NewEntrieRow>
                    <NewEntrieRow>
                      <span>Расплод</span>
                      <Controller
                        name="brood"
                        control={control}
                        rules={
                          formValues.hives.length > 1
                            ? undefined
                            : {min: {value: 1, message: 'Укажите расплод'}}
                        }
                        render={({field}) => (
                          <InputNumberStepper
                            name={field.name}
                            value={field.value}
                            disabled={formValues.hives.length > 1 ? 'all' : undefined}
                            defaultValue={field.value}
                            min={0}
                            max={formValues.strength}
                            onChange={onBroodChange}
                            error={errors.brood?.message}
                            hint={
                              formValues.hives.length > 1
                                ? field.value > 0
                                  ? 'Одинаковые знач.'
                                  : 'Разные значения'
                                : ''
                            }
                          />
                        )}
                      />
                      {/* {showBroodReset && 
              <Button 
                label="Сбросить"
                iconSuffix={<ResetIcon />}
                variant='ghost'
                colorScheme='neutral'
                onClick={() => onBroodChange(0)}
              />
              } */}
                    </NewEntrieRow>
                    <NewEntrieBorder />
                  </>
                )}

                <NewEntrieRow ref={dictionariesRef} $alignItems="flex-start">
                  <span>Состояние семьи</span>
                  <TagGroup
                    closable
                    editable
                    errorItems={commonDictionaryIds}
                    items={activeDictionaries.map((activeDictionary) => ({
                      value: activeDictionary.name,
                      key: activeDictionary.id || '',
                    }))}
                    onChangeInput={onActiveDictionariesInputChange}
                    onAdd={onActiveDictionaryAdd}
                    onRemove={onActiveDictionaryRemove}
                    inputProps={{
                      onFocus: () => setIsDictionariesPopoverOpened(true),
                      onClick: () => setIsDictionariesPopoverOpened(true),
                    }}
                  />

                  {Boolean(showDictionaries.length) && isDictionariesPopoverOpened && (
                    <NewEntrieDictionariesPopover className="_has-overflow">
                      {showDictionaries.map((showDictionary) => {
                        return (
                          <span
                            key={showDictionary.id}
                            onClick={() => onDictionaryClick(showDictionary)}
                          >
                            {showDictionary.name}
                          </span>
                        );
                      })}
                    </NewEntrieDictionariesPopover>
                  )}
                </NewEntrieRow>

                <NewEntrieBorder />

                <NewEntrieRow $alignItems="flex-start">
                  <span>Комментарий</span>
                  <Controller
                    name="note"
                    control={control}
                    rules={{
                      maxLength: {
                        value: 2000,
                        message: 'Не больше 2000 символов',
                      },
                    }}
                    render={({field}) => (
                      <NewEntrieTextAreaWrapper $height={noteHeight}>
                        <textarea
                          name={field.name}
                          value={field.value}
                          onChange={(event) => onNoteChange(event, field)}
                        />
                        <NewEntrieLength>{noteLength}/2000</NewEntrieLength>
                      </NewEntrieTextAreaWrapper>
                    )}
                  />
                </NewEntrieRow>

                <NewEntrieRow>
                  <span>
                    Вложения
                    {Boolean(attachments.length) && <i>{attachments.length}</i>}
                  </span>
                  <NewEntrieFilesWrapper>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      ref={fileInputRef}
                      onChange={onFileChange}
                    />

                    {attachments.map((attachment, index) => (
                      <NewEntrieImage key={index}>
                        <img
                          src={`/ecpp/attachments/files?attachmentId=${attachment}`}
                          alt="hive"
                        />
                        <NewEntrieDelete onClick={() => onFileRemove(index)}>
                          <CloseIcon />
                        </NewEntrieDelete>
                      </NewEntrieImage>
                    ))}

                    <NewEntriePlus onClick={() => fileInputRef.current?.click()}>
                      <AddLarge fill={COLORS.NEUTRAL.DEFAULT} />
                    </NewEntriePlus>
                  </NewEntrieFilesWrapper>
                </NewEntrieRow>
                <NewEntrieBorder />
                <DiaryCustomFields
                  editId={editId}
                  isSettings={false}
                  tempFieldIdsRef={tempFieldIdsRef}
                  customDictionaryType={customDictionaryType}
                  setCustomFields={setCustomFields}
                  customFields={customFields}
                  customFieldsCol={customFieldsCols}
                  setCustomFieldValue={setCustomFieldValue}
                  customFieldValues={customFieldValues}
                  customDictionaryIds={customDictionaryIds}
                  showDictionaries={customShowDictionaries}
                  isDictionariesPopoverOpened={customIsDictionariesPopoverOpened}
                  onActiveDictionariesInputChange={onCustomDictionariesInputChange}
                  onActiveDictionaryAdd={onCustomDictionaryAdd}
                  onActiveDictionaryRemove={onCustomDictionaryRemove}
                  onDictionaryClick={onCustomDictionaryClick}
                  setIsDictionariesPopoverOpened={setCustomIsDictionariesPopoverOpenedSafe}
                />
                {!entrieEvent ? (
                  <Button
                    label="Добавить напоминание"
                    iconPrefix={<AddLarge fill={COLORS.NEUTRAL.MUTED} />}
                    colorScheme="neutral"
                    variant="ghost"
                    onClick={() => setIsNewEventOpen(true)}
                  />
                ) : (
                  <>
                    <NewEntrieSubtitle>Напоминание</NewEntrieSubtitle>
                    <NewEntrieRow>
                      <span>Описание</span>
                      <div>{entrieEvent.description}</div>
                    </NewEntrieRow>
                    <NewEntrieRow>
                      <span>Начало</span>
                      <div>
                        {format(utcToUserDate(entrieEvent?.startAt, 'here2'), 'dd.MM.yyyy, HH:mm')}
                      </div>
                    </NewEntrieRow>
                    <NewEntrieRow>
                      <span>Окончание</span>
                      <div>{format(utcToUserDate(entrieEvent?.endAt), 'dd.MM.yyyy, HH:mm')}</div>
                    </NewEntrieRow>
                    <NewEntrieRow>
                      <span>Напоминание</span>
                      <div>{firstNotify}</div>
                    </NewEntrieRow>
                    <NewEntrieRow>
                      <span>2-ое напоминание</span>
                      <div>{secondNotify}</div>
                    </NewEntrieRow>
                    <Button
                      label="Изменить напоминание"
                      colorScheme="neutral"
                      variant="ghost"
                      onClick={() => setIsNewEventOpen(true)}
                    />
                  </>
                )}
              </>
            )}
          </NewEntrieBody>
          <NewEntrieFooter>
            <Button type="submit" label={editId ? 'Сохранить' : 'Создать запись'} />
            <Button label="Отменить" variant="ghost" colorScheme="neutral" onClick={onReset} />
          </NewEntrieFooter>
        </NewEntrieWrapper>
      </Drawer>
      {formValues.hives.length !== 0 && (
        <CalendarNewEvent
          onClose={() => setIsNewEventOpen(false)}
          isOpen={isТewEventOpen}
          onSubmit={onNewEventSubmit}
          isHivesDisabled
          editId={editEventValues?.id}
          defaultValues={calendarDefaultValues}
          selectedHives={formValues.hives}
        />
      )}
    </>
  );
};
